import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, User, Menu, X, LogOut, LayoutDashboard, Heart, FileText } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { NIVILABS_LOGO, BIONIVID_LOGO } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";

const CATEGORIES = [
  { slug: "enzymes", name: "Enzymes" },
  { slug: "reagents", name: "Reagents" },
  { slug: "kits", name: "Kits" },
  { slug: "consumables", name: "Consumables" },
  { slug: "instruments", name: "Instruments" },
];

const DEFAULT_PRODUCT_IMAGE = "https://nivilabs.bionivid.com/resources/images/assest.static.default.product.png";

export default function Header() {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!q || q.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/products/suggest`, { params: { q } });
        setSuggestions(data);
      } catch { /* empty */ }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setShowSug(false);
    nav(`/products?search=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b border-gray-100 shadow-sm" data-testid="site-header">

      <a href="https://bionivid.in" target="_blank" rel="noopener noreferrer" className="block">
        <div className="bg-green-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5 flex items-center justify-end gap-3 sm:gap-4">
            <img src={BIONIVID_LOGO} alt="Bionivid" className="h-8 sm:h-10 w-auto" />
            <div className="hidden sm:block">
              <div className="text-sm font-semibold text-white">Bionivid Technology</div>
              <div className="text-xs text-green-50">Trusted partner in life-science research</div>
            </div>
            <div className="sm:hidden text-sm font-semibold text-white">Bionivid Technology</div>
          </div>
        </div>
      </a>

      {/* Main bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center gap-6">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
          <img src={NIVILABS_LOGO} alt="nivilabs — a product of Bionivid" className="h-14 w-auto object-contain" />
        </Link>

        <form onSubmit={submitSearch} className="flex-1 max-w-2xl relative hidden md:block" data-testid="search-form">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => { setQ(e.target.value); setShowSug(true); }}
            onFocus={() => setShowSug(true)}
            onBlur={() => setTimeout(() => setShowSug(false), 150)}
            placeholder="Search enzymes, kits, reagents, Catalog No…"
            className="w-full h-11 pl-10 pr-4 rounded-full border border-gray-200 bg-muted/40 focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm"
          />
          {showSug && suggestions.length > 0 && (
            <div className="absolute z-50 mt-2 w-full bg-white border border-gray-100 shadow-lg rounded-xl overflow-hidden" data-testid="search-suggestions">
              {suggestions.map((s) => (
                <Link key={s.id} to={`/product/${s.id}`} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/60"
                  onClick={() => { setShowSug(false); setQ(""); }}>
                  <img src={s.images?.[0] || DEFAULT_PRODUCT_IMAGE} alt="" className="w-8 h-8 rounded object-cover" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-foreground">{s.name}</div>
                    <div className="text-xs text-muted-foreground">{s.catalogNumber}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </form>

        <div className="flex items-center gap-2 ml-auto">
          <button
            data-testid="open-cart-btn"
            onClick={() => setOpen(true)}
            className="relative inline-flex items-center gap-2 px-3 py-2 rounded-full border border-gray-200 hover:border-primary hover:text-primary transition-colors text-sm"
          >
            <ShoppingBag className="w-4 h-4" />
            <span className="hidden sm:inline">Inquiry</span>
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-accent text-accent-foreground text-[10px] font-bold flex items-center justify-center">
                {count}
              </span>
            )}
          </button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="user-menu-btn" className="rounded-full">
                  <User className="w-4 h-4 mr-1" /> {user.name?.split(" ")[0] || "Account"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" data-testid="user-menu">
                <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/account")} data-testid="menu-account">
                  <User className="w-4 h-4 mr-2" /> My Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/account/wishlist")} data-testid="menu-wishlist">
                  <Heart className="w-4 h-4 mr-2" /> Wishlist
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/account/inquiries")} data-testid="menu-inquiries">
                  <FileText className="w-4 h-4 mr-2" /> My Inquiries
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">
                      <LayoutDashboard className="w-4 h-4 mr-2" /> Admin Dashboard
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); nav("/"); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm" data-testid="login-link" className="hidden sm:inline-flex">
                <Link to="/login">Sign in</Link>
              </Button>
              <Button asChild size="sm" data-testid="register-link" className="bg-primary hover:bg-primary/90">
                <Link to="/register">Register</Link>
              </Button>
            </>
          )}

          <button className="md:hidden p-2" onClick={() => setMobileOpen((v) => !v)} data-testid="mobile-toggle">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Category nav */}
      <nav className="border-t border-gray-100 bg-white hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-12 flex items-center gap-6 text-sm overflow-x-auto">
          {CATEGORIES.map((c) => (
            <NavLink
              key={c.slug}
              to={`/products?category=${c.slug}`}
              data-testid={`nav-cat-${c.slug}`}
              className={({ isActive }) =>
                `whitespace-nowrap font-medium hover:text-primary transition-colors ${isActive ? "text-primary" : "text-foreground/70"}`
              }
            >
              {c.name}
            </NavLink>
          ))}
          <span className="ml-auto text-xs text-muted-foreground">
            <div className="hidden md:flex items-center gap-5 opacity-90">
              {/* <Link to="/resources" className="hover:opacity-100">Resources</Link> */}
              <Link to="/about" className="hover:opacity-100">About</Link>
              <Link to="/contact" className="hover:opacity-100">Contact</Link>
            </div>
          </span>
        </div>
      </nav>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 p-4 space-y-3" data-testid="mobile-menu">
          <form onSubmit={submitSearch} className="relative" data-testid="mobile-search-form">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => { setQ(e.target.value); setShowSug(true); }}
              onFocus={() => setShowSug(true)}
              onBlur={() => setTimeout(() => setShowSug(false), 150)}
              placeholder="Search…"
              className="w-full h-10 pl-10 pr-3 rounded-md border border-gray-200"
              data-testid="mobile-search-input"
            />
            {showSug && suggestions.length > 0 && (
              <div className="absolute left-0 right-0 top-full z-50 mt-2 bg-white border border-gray-100 shadow-lg rounded-xl overflow-hidden" data-testid="mobile-search-suggestions">
                {suggestions.map((s) => (
                  <Link
                    key={s.id}
                    to={`/product/${s.id}`}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-muted/60"
                    onClick={() => { setShowSug(false); setQ(""); setMobileOpen(false); }}
                  >
                    <img src={s.images?.[0] || DEFAULT_PRODUCT_IMAGE} alt="" className="w-8 h-8 rounded object-cover" />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-foreground">{s.name}</div>
                      <div className="text-xs text-muted-foreground">{s.catalogNumber}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </form>
          {CATEGORIES.map((c) => (
            <Link key={c.slug} to={`/products?category=${c.slug}`} onClick={() => setMobileOpen(false)}
              className="block py-1.5 text-sm font-medium">{c.name}</Link>
          ))}
          {!user && (
            <div className="pt-2 flex gap-2">
              <Button asChild className="flex-1"><Link to="/login">Sign in</Link></Button>
              <Button asChild variant="outline" className="flex-1"><Link to="/register">Register</Link></Button>
            </div>
          )}
        </div>
      )}
    </header>
  );
}